// 1:1 TypeScript port of manager/static/lib/probes.js. Behaviour MUST stay
// identical to the original JS module — the credibility scorer (TS port in
// @fingerprintclf/shared) was tuned against payloads produced by this exact
// probe set. If you tweak a probe, mirror it back to manager/static if the
// Python copy still exists, otherwise update the scorer fixtures.

import { djb2Hash, fmtPrec, attachedCanvas, detachCanvas } from './util.js';

// Loose typing — probes intentionally tolerate vendor-specific fields so the
// scorer can read whatever the browser exposed without losing data.
type Dict = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Navigator
// ---------------------------------------------------------------------------
export function probeNavigator(): Dict {
  const nav: Dict = {};
  try {
    const n = navigator as Navigator & { deviceMemory?: number; doNotTrack?: string | null };
    nav['userAgent'] = n.userAgent || '';
    nav['platform'] = n.platform || '';
    nav['language'] = n.language || '';
    nav['languages'] = n.languages ? Array.from(n.languages) : [];
    nav['hardwareConcurrency'] = n.hardwareConcurrency || 0;
    nav['deviceMemory'] = n.deviceMemory || 0;
    nav['maxTouchPoints'] = n.maxTouchPoints || 0;
    nav['vendor'] = n.vendor || '';
    nav['doNotTrack'] = n.doNotTrack ?? null;
    nav['webdriver'] = !!n.webdriver;
    nav['cookieEnabled'] = !!n.cookieEnabled;
    nav['productSub'] = n.productSub || '';
    const w = window as unknown as { chrome?: { runtime?: { id?: string } } };
    nav['_hasChrome'] = !!w.chrome;
    nav['_hasRuntime'] = !!(w.chrome && w.chrome.runtime && w.chrome.runtime.id);
  } catch { /* keep partial result */ }
  return nav;
}

// ---------------------------------------------------------------------------
// WebGL1
// ---------------------------------------------------------------------------
export function probeWebGL1(): Dict {
  const result: Dict = {
    vendor: '', renderer: '', params: {} as Dict, extensions: [] as string[],
    status: 'init', attempts: [] as Dict[],
  };
  let gl: WebGLRenderingContext | null = null;
  let canvas: HTMLCanvasElement | null = null;
  try {
    canvas = attachedCanvas(256, 128);
    const strategies: Array<[string, WebGLContextAttributes | undefined]> = [
      ['webgl', undefined],
      ['webgl', { antialias: false, preserveDrawingBuffer: false }],
      ['webgl', { failIfMajorPerformanceCaveat: false, antialias: false }],
      ['webgl', { powerPreference: 'low-power', failIfMajorPerformanceCaveat: false }],
      ['experimental-webgl', undefined],
    ];
    for (let si = 0; si < strategies.length; si += 1) {
      const tname = strategies[si]![0];
      const topts = strategies[si]![1];
      try {
        const cand = (topts === undefined
          ? canvas.getContext(tname)
          : canvas.getContext(tname, topts)) as WebGLRenderingContext | null;
        (result['attempts'] as Dict[]).push({ name: tname, ok: !!cand });
        if (cand) { gl = cand; result['status'] = `got-ctx:${si}`; break; }
      } catch (e) {
        (result['attempts'] as Dict[]).push({ name: tname, ok: false, err: errMsg(e) });
      }
    }
    if (!gl) { result['status'] = 'all-attempts-null'; return result; }
    if (gl.isContextLost && gl.isContextLost()) { result['status'] = 'ctx-lost-immediately'; return result; }

    const ext = gl.getExtension('WEBGL_debug_renderer_info') as { UNMASKED_VENDOR_WEBGL: number; UNMASKED_RENDERER_WEBGL: number } | null;
    const params = result['params'] as Dict;
    if (ext) {
      result['vendor'] = gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) || '';
      result['renderer'] = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '';
      params['UNMASKED_VENDOR_WEBGL'] = result['vendor'];
      params['UNMASKED_RENDERER_WEBGL'] = result['renderer'];
    }

    const paramNames: Record<number, string> = {};
    paramNames[gl.MAX_TEXTURE_SIZE] = 'MAX_TEXTURE_SIZE';
    paramNames[gl.MAX_CUBE_MAP_TEXTURE_SIZE] = 'MAX_CUBE_MAP_TEXTURE_SIZE';
    paramNames[gl.MAX_RENDERBUFFER_SIZE] = 'MAX_RENDERBUFFER_SIZE';
    paramNames[gl.MAX_TEXTURE_IMAGE_UNITS] = 'MAX_TEXTURE_IMAGE_UNITS';
    paramNames[gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS] = 'MAX_VERTEX_TEXTURE_IMAGE_UNITS';
    paramNames[gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS] = 'MAX_COMBINED_TEXTURE_IMAGE_UNITS';
    paramNames[gl.MAX_VERTEX_ATTRIBS] = 'MAX_VERTEX_ATTRIBS';
    paramNames[gl.MAX_VERTEX_UNIFORM_VECTORS] = 'MAX_VERTEX_UNIFORM_VECTORS';
    paramNames[gl.MAX_FRAGMENT_UNIFORM_VECTORS] = 'MAX_FRAGMENT_UNIFORM_VECTORS';
    paramNames[gl.MAX_VARYING_VECTORS] = 'MAX_VARYING_VECTORS';
    paramNames[gl.MAX_VIEWPORT_DIMS] = 'MAX_VIEWPORT_DIMS';
    paramNames[gl.ALIASED_POINT_SIZE_RANGE] = 'ALIASED_POINT_SIZE_RANGE';
    paramNames[gl.ALIASED_LINE_WIDTH_RANGE] = 'ALIASED_LINE_WIDTH_RANGE';
    paramNames[gl.STENCIL_BITS] = 'STENCIL_BITS';
    paramNames[gl.SUBPIXEL_BITS] = 'SUBPIXEL_BITS';
    paramNames[gl.RED_BITS] = 'RED_BITS';
    paramNames[gl.GREEN_BITS] = 'GREEN_BITS';
    paramNames[gl.BLUE_BITS] = 'BLUE_BITS';
    paramNames[gl.ALPHA_BITS] = 'ALPHA_BITS';
    paramNames[gl.DEPTH_BITS] = 'DEPTH_BITS';
    paramNames[gl.SAMPLES] = 'SAMPLES';
    paramNames[gl.SAMPLE_BUFFERS] = 'SAMPLE_BUFFERS';

    for (const enumStr of Object.keys(paramNames)) {
      const enumVal = parseInt(enumStr, 10);
      const name = paramNames[enumVal]!;
      try {
        const val: unknown = gl.getParameter(enumVal);
        if (val instanceof Float32Array || val instanceof Int32Array) {
          params[name] = Array.from(val);
        } else {
          params[name] = val;
        }
      } catch {
        params[name] = null;
      }
    }
    params['VERSION'] = gl.getParameter(gl.VERSION);
    params['SHADING_LANGUAGE_VERSION'] = gl.getParameter(gl.SHADING_LANGUAGE_VERSION);
    params['VENDOR'] = gl.getParameter(gl.VENDOR);
    params['RENDERER'] = gl.getParameter(gl.RENDERER);

    const shaders: Array<[number, string]> = [
      [gl.VERTEX_SHADER, 'VERTEX_SHADER'],
      [gl.FRAGMENT_SHADER, 'FRAGMENT_SHADER'],
    ];
    const precisions: Array<[number, string]> = [
      [gl.HIGH_FLOAT, 'HIGH_FLOAT'], [gl.MEDIUM_FLOAT, 'MEDIUM_FLOAT'], [gl.LOW_FLOAT, 'LOW_FLOAT'],
      [gl.HIGH_INT, 'HIGH_INT'], [gl.MEDIUM_INT, 'MEDIUM_INT'], [gl.LOW_INT, 'LOW_INT'],
    ];
    for (const [s, sn] of shaders) {
      for (const [p, pn] of precisions) {
        params[`${sn}_${pn}`] = fmtPrec(gl.getShaderPrecisionFormat(s, p));
      }
    }

    result['extensions'] = gl.getSupportedExtensions() || [];
    result['status'] = (gl.isContextLost && gl.isContextLost()) ? 'ctx-lost-during-probe' : 'ok';
  } catch (e) {
    result['status'] = `exception:${errMsg(e)}`;
  } finally {
    if (gl) {
      try {
        const lose = gl.getExtension('WEBGL_lose_context') as { loseContext(): void } | null;
        if (lose) lose.loseContext();
      } catch { /* ok */ }
    }
    detachCanvas(canvas);
  }
  return result;
}

// ---------------------------------------------------------------------------
// WebGL2
// ---------------------------------------------------------------------------
export function probeWebGL2(): Dict {
  const result: Dict = {
    params2: {} as Dict, extensions2: [] as string[],
    status: 'init', attempts: [] as Dict[],
  };
  let gl2: WebGL2RenderingContext | null = null;
  let canvas: HTMLCanvasElement | null = null;
  try {
    canvas = attachedCanvas(256, 128);
    const strategies: Array<WebGLContextAttributes | undefined> = [
      undefined,
      { antialias: false, preserveDrawingBuffer: false },
      { failIfMajorPerformanceCaveat: false, antialias: false },
      { powerPreference: 'low-power', failIfMajorPerformanceCaveat: false },
    ];
    for (let si = 0; si < strategies.length; si += 1) {
      const topts = strategies[si];
      try {
        const cand = (topts === undefined
          ? canvas.getContext('webgl2')
          : canvas.getContext('webgl2', topts)) as WebGL2RenderingContext | null;
        (result['attempts'] as Dict[]).push({ ok: !!cand });
        if (cand) { gl2 = cand; result['status'] = `got-ctx:${si}`; break; }
      } catch (e) {
        (result['attempts'] as Dict[]).push({ ok: false, err: errMsg(e) });
      }
    }
    if (!gl2) { result['status'] = 'all-attempts-null'; return result; }
    if (gl2.isContextLost && gl2.isContextLost()) { result['status'] = 'ctx-lost-immediately'; return result; }

    const params2 = result['params2'] as Dict;
    const gl2Params: Record<number, string> = {};
    gl2Params[gl2.MAX_3D_TEXTURE_SIZE] = 'MAX_3D_TEXTURE_SIZE';
    gl2Params[gl2.MAX_ARRAY_TEXTURE_LAYERS] = 'MAX_ARRAY_TEXTURE_LAYERS';
    gl2Params[gl2.MAX_COLOR_ATTACHMENTS] = 'MAX_COLOR_ATTACHMENTS';
    gl2Params[gl2.MAX_DRAW_BUFFERS] = 'MAX_DRAW_BUFFERS';
    gl2Params[gl2.MAX_FRAGMENT_INPUT_COMPONENTS] = 'MAX_FRAGMENT_INPUT_COMPONENTS';
    gl2Params[gl2.MAX_FRAGMENT_UNIFORM_BLOCKS] = 'MAX_FRAGMENT_UNIFORM_BLOCKS';
    gl2Params[gl2.MAX_FRAGMENT_UNIFORM_COMPONENTS] = 'MAX_FRAGMENT_UNIFORM_COMPONENTS';
    gl2Params[gl2.MAX_SAMPLES] = 'MAX_SAMPLES';
    gl2Params[gl2.MAX_SERVER_WAIT_TIMEOUT] = 'MAX_SERVER_WAIT_TIMEOUT';
    gl2Params[gl2.MAX_TEXTURE_LOD_BIAS] = 'MAX_TEXTURE_LOD_BIAS';
    gl2Params[gl2.MAX_TRANSFORM_FEEDBACK_INTERLEAVED_COMPONENTS] = 'MAX_TRANSFORM_FEEDBACK_INTERLEAVED_COMPONENTS';
    gl2Params[gl2.MAX_TRANSFORM_FEEDBACK_SEPARATE_ATTRIBS] = 'MAX_TRANSFORM_FEEDBACK_SEPARATE_ATTRIBS';
    gl2Params[gl2.MAX_TRANSFORM_FEEDBACK_SEPARATE_COMPONENTS] = 'MAX_TRANSFORM_FEEDBACK_SEPARATE_COMPONENTS';
    gl2Params[gl2.MAX_UNIFORM_BLOCK_SIZE] = 'MAX_UNIFORM_BLOCK_SIZE';
    gl2Params[gl2.MAX_UNIFORM_BUFFER_BINDINGS] = 'MAX_UNIFORM_BUFFER_BINDINGS';
    gl2Params[gl2.MAX_VARYING_COMPONENTS] = 'MAX_VARYING_COMPONENTS';
    gl2Params[gl2.MAX_VERTEX_OUTPUT_COMPONENTS] = 'MAX_VERTEX_OUTPUT_COMPONENTS';
    gl2Params[gl2.MAX_VERTEX_UNIFORM_BLOCKS] = 'MAX_VERTEX_UNIFORM_BLOCKS';
    gl2Params[gl2.MAX_VERTEX_UNIFORM_COMPONENTS] = 'MAX_VERTEX_UNIFORM_COMPONENTS';
    gl2Params[gl2.MIN_PROGRAM_TEXEL_OFFSET] = 'MIN_PROGRAM_TEXEL_OFFSET';
    gl2Params[gl2.MAX_PROGRAM_TEXEL_OFFSET] = 'MAX_PROGRAM_TEXEL_OFFSET';
    gl2Params[gl2.MAX_ELEMENT_INDEX] = 'MAX_ELEMENT_INDEX';
    // WebGL1-shared params via WebGL2 context
    gl2Params[gl2.MAX_TEXTURE_SIZE] = 'MAX_TEXTURE_SIZE';
    gl2Params[gl2.MAX_CUBE_MAP_TEXTURE_SIZE] = 'MAX_CUBE_MAP_TEXTURE_SIZE';
    gl2Params[gl2.MAX_RENDERBUFFER_SIZE] = 'MAX_RENDERBUFFER_SIZE';
    gl2Params[gl2.MAX_TEXTURE_IMAGE_UNITS] = 'MAX_TEXTURE_IMAGE_UNITS';
    gl2Params[gl2.MAX_VERTEX_TEXTURE_IMAGE_UNITS] = 'MAX_VERTEX_TEXTURE_IMAGE_UNITS';
    gl2Params[gl2.MAX_COMBINED_TEXTURE_IMAGE_UNITS] = 'MAX_COMBINED_TEXTURE_IMAGE_UNITS';
    gl2Params[gl2.MAX_VERTEX_ATTRIBS] = 'MAX_VERTEX_ATTRIBS';
    gl2Params[gl2.MAX_VERTEX_UNIFORM_VECTORS] = 'MAX_VERTEX_UNIFORM_VECTORS';
    gl2Params[gl2.MAX_FRAGMENT_UNIFORM_VECTORS] = 'MAX_FRAGMENT_UNIFORM_VECTORS';
    gl2Params[gl2.MAX_VARYING_VECTORS] = 'MAX_VARYING_VECTORS';

    for (const enumStr of Object.keys(gl2Params)) {
      const enumVal = parseInt(enumStr, 10);
      const name = gl2Params[enumVal]!;
      try {
        const val: unknown = gl2.getParameter(enumVal);
        if (val instanceof Float32Array || val instanceof Int32Array) {
          params2[name] = Array.from(val);
        } else {
          params2[name] = val;
        }
      } catch {
        params2[name] = null;
      }
    }
    params2['VERSION'] = gl2.getParameter(gl2.VERSION);
    params2['SHADING_LANGUAGE_VERSION'] = gl2.getParameter(gl2.SHADING_LANGUAGE_VERSION);
    params2['VENDOR'] = gl2.getParameter(gl2.VENDOR);
    params2['RENDERER'] = gl2.getParameter(gl2.RENDERER);
    params2['VERTEX_SHADER_HIGH_FLOAT'] = fmtPrec(gl2.getShaderPrecisionFormat(gl2.VERTEX_SHADER, gl2.HIGH_FLOAT));
    params2['FRAGMENT_SHADER_HIGH_FLOAT'] = fmtPrec(gl2.getShaderPrecisionFormat(gl2.FRAGMENT_SHADER, gl2.HIGH_FLOAT));

    const ext = gl2.getExtension('WEBGL_debug_renderer_info') as { UNMASKED_VENDOR_WEBGL: number; UNMASKED_RENDERER_WEBGL: number } | null;
    if (ext) {
      params2['UNMASKED_VENDOR_WEBGL'] = gl2.getParameter(ext.UNMASKED_VENDOR_WEBGL) || '';
      params2['UNMASKED_RENDERER_WEBGL'] = gl2.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '';
    }

    result['extensions2'] = gl2.getSupportedExtensions() || [];
    result['status'] = (gl2.isContextLost && gl2.isContextLost()) ? 'ctx-lost-during-probe' : 'ok';
  } catch (e) {
    result['status'] = `exception:${errMsg(e)}`;
  } finally {
    if (gl2) {
      try {
        const lose = gl2.getExtension('WEBGL_lose_context') as { loseContext(): void } | null;
        if (lose) lose.loseContext();
      } catch { /* ok */ }
    }
    detachCanvas(canvas);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Canvas hash
// ---------------------------------------------------------------------------
export function probeCanvas(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 280; canvas.height = 60;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, 280, 60);
    ctx.font = '14px Arial';
    ctx.fillStyle = '#e94560';
    ctx.fillText('Browser Compatibility Check', 5, 20);
    ctx.font = 'italic 12px Georgia';
    ctx.fillStyle = '#0f3460';
    ctx.fillText('canvas-fp-2024', 5, 45);
    ctx.fillStyle = 'rgba(102,204,0,0.7)';
    ctx.fillRect(100, 5, 80, 50);
    ctx.strokeStyle = '#16213e';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(220, 30, 20, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,107,107,0.5)';
    ctx.beginPath();
    ctx.arc(220, 30, 15, 0, Math.PI * 2);
    ctx.fill();
    return djb2Hash(canvas.toDataURL('image/png'));
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Audio fingerprint
// ---------------------------------------------------------------------------
type LegacyAudioCtor = new () => AudioContext;
declare global {
  interface Window {
    webkitAudioContext?: LegacyAudioCtor;
  }
}

export function probeAudio(externalCtx?: AudioContext): Promise<Dict> {
  return new Promise((resolve) => {
    const result: Dict = {
      hash: '', sampleRate: 0, channelCount: 0, maxChannelCount: 0,
      state: '', supported: false, status: 'init',
    };
    let ctx: AudioContext | null = null;
    let ownsCtx = false;
    try {
      if (externalCtx) {
        ctx = externalCtx;
        result['status'] = 'external-ctx';
      } else {
        const Ctor = (window.AudioContext ?? window.webkitAudioContext) as LegacyAudioCtor | undefined;
        if (!Ctor) { result['status'] = 'no-api'; resolve(result); return; }
        ctx = new Ctor();
        ownsCtx = true;
        result['status'] = 'ctx-created';
      }
      result['sampleRate'] = ctx.sampleRate || 0;
      result['channelCount'] = (ctx.destination && ctx.destination.channelCount) || 0;
      result['maxChannelCount'] = (ctx.destination && ctx.destination.maxChannelCount) || 0;
      result['state'] = ctx.state || '';
      result['supported'] = true;
    } catch (e) {
      result['status'] = `ctor-exception:${errMsg(e)}`;
      resolve(result);
      return;
    }
    const ctxRef = ctx;
    const runPipeline = () => {
      result['state'] = ctxRef.state || result['state'];
      try {
        const oscillator = ctxRef.createOscillator();
        const compressor = ctxRef.createDynamicsCompressor();
        const analyser = ctxRef.createAnalyser();
        analyser.fftSize = 256;
        compressor.threshold.setValueAtTime(-50, ctxRef.currentTime);
        compressor.knee.setValueAtTime(40, ctxRef.currentTime);
        compressor.ratio.setValueAtTime(12, ctxRef.currentTime);
        compressor.attack.setValueAtTime(0, ctxRef.currentTime);
        compressor.release.setValueAtTime(0.25, ctxRef.currentTime);
        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(10000, ctxRef.currentTime);
        oscillator.connect(compressor);
        compressor.connect(analyser);
        analyser.connect(ctxRef.destination);
        oscillator.start(0);
        setTimeout(() => {
          try {
            const freqData = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(freqData);
            let s = '';
            for (let i = 0; i < freqData.length; i += 1) s += freqData[i]!.toString(16);
            result['hash'] = djb2Hash(s);
            result['state'] = ctxRef.state || result['state'];
            result['status'] = result['hash'] ? 'ok' : `hash-empty-state:${result['state']}`;
          } catch (e) {
            result['status'] = `hash-exception:${errMsg(e)}`;
          }
          try { oscillator.stop(); } catch { /* ok */ }
          try { oscillator.disconnect(); } catch { /* ok */ }
          try { compressor.disconnect(); } catch { /* ok */ }
          try { analyser.disconnect(); } catch { /* ok */ }
          if (ownsCtx) { try { ctxRef.close(); } catch { /* ok */ } }
          resolve(result);
        }, 100);
      } catch (e) {
        result['status'] = `pipeline-exception:${errMsg(e)}`;
        if (ownsCtx) { try { ctxRef.close(); } catch { /* ok */ } }
        resolve(result);
      }
    };
    if (ctxRef.state === 'suspended' && typeof ctxRef.resume === 'function') {
      // iOS Safari: ctx.resume() outside a user gesture may never settle.
      // 1.5s watchdog so the wider collection isn't blocked.
      let settled = false;
      const t = setTimeout(() => {
        if (settled) return;
        settled = true;
        result['status'] = 'resume-timeout';
        runPipeline();
      }, 1500);
      const onSettled = () => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        runPipeline();
      };
      try {
        ctxRef.resume().then(onSettled, onSettled);
      } catch {
        onSettled();
      }
    } else {
      runPipeline();
    }
  });
}

// ---------------------------------------------------------------------------
// Speech voices
// ---------------------------------------------------------------------------
export function probeVoices(): Promise<Dict[]> {
  return new Promise((resolve) => {
    if (!window.speechSynthesis) { resolve([]); return; }
    // voiceURI is the OS-native canonical id (macOS:
    // "com.apple.voice.compact.en-US.Samantha", Windows:
    // "Microsoft David - English (United States)"). The ingest pipeline
    // (manager/database.py promote_fingerprint → device_template.voices_json
    // → C++ speech_synthesis.cc) reads voiceURI alongside name/lang to
    // reconstruct each voice exactly. Without voiceURI the C++ side
    // falls back to using `name` as the URI, which exposes a
    // non-reverse-domain string and lets CreepJS / Pixelscan flag the
    // profile as "non-real macOS".
    const get = (): Dict[] =>
      window.speechSynthesis.getVoices().map((v) => ({
        name: v.name,
        lang: v.lang,
        localService: v.localService,
        default: v.default,
        voiceURI: v.voiceURI,
      }));
    const initial = get();
    if (initial.length > 0) { resolve(initial); return; }
    let resolved = false;
    window.speechSynthesis.onvoiceschanged = () => {
      if (resolved) return;
      resolved = true;
      resolve(get());
    };
    setTimeout(() => {
      if (resolved) return;
      resolved = true;
      resolve(get());
    }, 1500);
  });
}

// ---------------------------------------------------------------------------
// Screen / window / orientation / media-queries / timezone
// ---------------------------------------------------------------------------
export function probeScreen(): Dict {
  try {
    return {
      width: screen.width, height: screen.height,
      availWidth: screen.availWidth, availHeight: screen.availHeight,
      colorDepth: screen.colorDepth, pixelDepth: screen.pixelDepth,
      devicePixelRatio: window.devicePixelRatio || 1,
    };
  } catch { return {}; }
}

export function probeClientHints(): Promise<Dict | null> {
  return new Promise((resolve) => {
    try {
      const uad = (navigator as unknown as { userAgentData?: { getHighEntropyValues(hints: string[]): Promise<Dict> } }).userAgentData;
      if (!uad || !uad.getHighEntropyValues) { resolve(null); return; }
      uad.getHighEntropyValues([
        'architecture', 'bitness', 'brands', 'fullVersionList',
        'mobile', 'model', 'platform', 'platformVersion',
      ]).then((hints) => {
        resolve({
          architecture: hints['architecture'] || '',
          bitness: hints['bitness'] || '',
          brands: hints['brands'] || [],
          fullVersionList: hints['fullVersionList'] || [],
          mobile: hints['mobile'] || false,
          model: hints['model'] || '',
          platform: hints['platform'] || '',
          platformVersion: hints['platformVersion'] || '',
        });
      }).catch(() => resolve(null));
    } catch {
      resolve(null);
    }
  });
}

export function probeFonts(): string[] {
  const detected: string[] = [];
  try {
    // Candidate list — covers macOS pre-installed system fonts (Apple
    // SD Gothic Neo, Hiragino Sans, PingFang, Helvetica, Geneva, etc.),
    // Windows core fonts (Segoe UI, Calibri, Cambria), Linux fallback
    // fonts, and a few legacy Office-bundled families. Detector sites
    // compare the resulting list against per-OS reference sets — too
    // few candidates and the answer collapses to "uniformly small set",
    // making spoofed profiles indistinguishable from kiosk browsers.
    // ~140 entries puts us in the same bucket as honest macOS Chrome.
    //
    // Stays in sync with scripts/collect_device_fingerprint.py's
    // `candidates` list — same set, same order. Don't reorder; some
    // detectors hash this list raw.
    const testFonts = [
      // macOS core
      'Al Bayan', 'Al Nile', 'American Typewriter', 'Andale Mono',
      'Apple Chancery', 'Apple SD Gothic Neo', 'Apple Symbols',
      'AppleGothic', 'AppleMyungjo',
      'Arial', 'Arial Black', 'Arial Hebrew', 'Arial Narrow',
      'Arial Rounded MT Bold',
      'Avenir', 'Avenir Next', 'Avenir Next Condensed',
      'Ayuthaya', 'Baghdad', 'Bangla MN', 'Baskerville', 'Beirut',
      'Big Caslon', 'Bodoni 72',
      'Bradley Hand', 'Brush Script MT',
      'Chalkboard', 'Chalkboard SE', 'Chalkduster',
      'Charter', 'Cochin', 'Comic Sans MS', 'Copperplate',
      'Corsiva Hebrew',
      'Courier', 'Courier New',
      'Damascus', 'DecoType Naskh', 'Devanagari MT',
      'Didot', 'DIN Alternate', 'DIN Condensed',
      'Diwan Kufi', 'Diwan Thuluth',
      'Euphemia UCAS', 'Farah', 'Farisi',
      'Futura', 'Galvji', 'GB18030 Bitmap',
      'Geeza Pro', 'Geneva', 'Georgia',
      'Gill Sans', 'Grantha Sangam MN', 'Gujarati MT',
      'Gurmukhi MN', 'Gurmukhi MT', 'Gurmukhi Sangam MN',
      'Heiti SC', 'Heiti TC',
      'Helvetica', 'Helvetica Neue',
      'Herculanum', 'Hiragino Sans', 'Hoefler Text',
      'Impact', 'InaiMathi', 'Iowan Old Style', 'ITF Devanagari',
      'Kailasa', 'Kannada MN', 'Kefa', 'Khmer MN',
      'Kohinoor Bangla', 'Kohinoor Devanagari',
      'Kokonor', 'Krungthep', 'KufiStandardGK',
      'Lao MN', 'Lao Sangam MN',
      'Lucida Grande', 'Luminari',
      'Malayalam MN', 'Marker Felt', 'Menlo',
      'Microsoft Sans Serif', 'Mishafi', 'Monaco',
      'Mshtakan', 'Mukta Mahee', 'Muna', 'Myanmar MN',
      'Nadeem', 'New Peninim MT', 'Noteworthy',
      'Optima', 'Oriya MN', 'Oriya Sangam MN',
      'PT Mono', 'PT Sans', 'PT Serif',
      'Palatino', 'Papyrus', 'Party LET',
      'PingFang HK', 'PingFang SC', 'PingFang TC',
      'Plantagenet Cherokee',
      'Raanana', 'Rockwell',
      'Sana', 'Sathu', 'Savoye LET', 'Seravek',
      'Shree Devanagari 714',
      'SignPainter', 'Silom',
      'Sinhala MN', 'Sinhala Sangam MN',
      'Skia', 'Snell Roundhand',
      'Songti SC', 'Songti TC',
      'STIX Two Math', 'STIX Two Text', 'Sukhumvit Set',
      'Symbol', 'Tahoma',
      'Tamil MN', 'Tamil Sangam MN',
      'Telugu MN', 'Telugu Sangam MN',
      'Thonburi', 'Times', 'Times New Roman',
      'Trattatello', 'Trebuchet MS',
      'Verdana', 'Webdings',
      'Wingdings', 'Wingdings 2', 'Wingdings 3',
      'Zapf Dingbats', 'Zapfino',
      // CJK localized aliases — macOS exposes both ASCII and localized
      // names for the same family; testing both surfaces the user's
      // system locale variant (a strong cohort signal).
      '苹方-简', '苹方-繁', '苹方-港',
      '儷宋 Pro', '儷黑 Pro',
      '蘋方-港', '蘋方-繁', '蘋果儷中黑', '蘋果儷細宋',
      // Windows core (cross-platform completeness)
      'Segoe UI', 'Calibri', 'Cambria', 'Consolas',
      'Candara', 'Constantia', 'Corbel',
      'Franklin Gothic Medium', 'Perpetua',
      'Book Antiqua', 'Garamond', 'Lucida Console',
    ];
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testString = 'mmmmmmmmmmlli!@#$%^&*()WwQq10';
    const testSize = '72px';
    const canvas = document.createElement('canvas');
    canvas.width = 600; canvas.height = 100;
    const ctx = canvas.getContext('2d');
    if (!ctx) return detected;
    const baseWidths: Record<string, number> = {};
    for (const b of baseFonts) {
      ctx.font = `${testSize} ${b}`;
      baseWidths[b] = ctx.measureText(testString).width;
    }
    for (const fontName of testFonts) {
      for (const b of baseFonts) {
        ctx.font = `${testSize} "${fontName}", ${b}`;
        if (ctx.measureText(testString).width !== baseWidths[b]) {
          detected.push(fontName);
          break;
        }
      }
    }
  } catch { /* swallow */ }
  return detected;
}

export function probeTimezone(): Dict {
  try {
    const r = Intl.DateTimeFormat().resolvedOptions();
    return {
      name: r.timeZone || '',
      offsetMinutes: -new Date().getTimezoneOffset(),
      locale: r.locale || '',
      dateResolved: r,
    };
  } catch {
    return { name: '', offsetMinutes: 0, locale: '', dateResolved: null };
  }
}

export function probePerformanceMemory(): Dict {
  try {
    const m = (performance as unknown as { memory?: { jsHeapSizeLimit?: number; totalJSHeapSize?: number; usedJSHeapSize?: number } }).memory;
    if (!m) return { jsHeapSizeLimit: 0, totalJSHeapSize: 0, usedJSHeapSize: 0, supported: false };
    return {
      jsHeapSizeLimit: m.jsHeapSizeLimit || 0,
      totalJSHeapSize: m.totalJSHeapSize || 0,
      usedJSHeapSize: m.usedJSHeapSize || 0,
      supported: true,
    };
  } catch {
    return { jsHeapSizeLimit: 0, totalJSHeapSize: 0, usedJSHeapSize: 0, supported: false };
  }
}

export function probeConnection(): Dict {
  try {
    const c = (navigator as unknown as {
      connection?: Dict; mozConnection?: Dict; webkitConnection?: Dict;
    }).connection ?? (navigator as unknown as { mozConnection?: Dict }).mozConnection ?? (navigator as unknown as { webkitConnection?: Dict }).webkitConnection;
    if (!c) return { effectiveType: '', downlink: 0, rtt: 0, saveData: false, type: '', supported: false };
    return {
      effectiveType: (c['effectiveType'] as string) || '',
      downlink: typeof c['downlink'] === 'number' ? c['downlink'] : 0,
      rtt: typeof c['rtt'] === 'number' ? c['rtt'] : 0,
      saveData: !!c['saveData'],
      type: (c['type'] as string) || '',
      supported: true,
    };
  } catch {
    return { effectiveType: '', downlink: 0, rtt: 0, saveData: false, type: '', supported: false };
  }
}

export function probeBattery(): Promise<Dict> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: Dict) => { if (done) return; done = true; resolve(v); };
    setTimeout(() => finish({ charging: false, chargingTime: 0, dischargingTime: 0, level: 0, supported: false }), 2000);
    try {
      const getBat = (navigator as unknown as { getBattery?: () => Promise<Dict> }).getBattery;
      if (!getBat) {
        finish({ charging: false, chargingTime: 0, dischargingTime: 0, level: 0, supported: false });
        return;
      }
      getBat.call(navigator).then((b) => {
        finish({
          charging: !!b['charging'],
          chargingTime: typeof b['chargingTime'] === 'number' && Number.isFinite(b['chargingTime']) ? b['chargingTime'] : -1,
          dischargingTime: typeof b['dischargingTime'] === 'number' && Number.isFinite(b['dischargingTime']) ? b['dischargingTime'] : -1,
          level: typeof b['level'] === 'number' ? b['level'] : 0,
          supported: true,
        });
      }).catch(() => finish({ charging: false, chargingTime: 0, dischargingTime: 0, level: 0, supported: false }));
    } catch {
      finish({ charging: false, chargingTime: 0, dischargingTime: 0, level: 0, supported: false });
    }
  });
}

export function probeMediaDevices(): Promise<Dict> {
  return new Promise((resolve) => {
    let done = false;
    const fallback = { devices: [], audioinput: 0, audiooutput: 0, videoinput: 0, labelsVisible: false, supported: false };
    const finish = (v: Dict) => { if (done) return; done = true; resolve(v); };
    setTimeout(() => finish(fallback), 2000);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        finish(fallback); return;
      }
      navigator.mediaDevices.enumerateDevices().then((list) => {
        const summary = { audioinput: 0, audiooutput: 0, videoinput: 0 };
        let labelsVisible = false;
        const devices: Dict[] = [];
        for (const d of list) {
          if (d.kind === 'audioinput') summary.audioinput += 1;
          else if (d.kind === 'audiooutput') summary.audiooutput += 1;
          else if (d.kind === 'videoinput') summary.videoinput += 1;
          if (d.label) labelsVisible = true;
          devices.push({
            kind: d.kind || '',
            deviceIdHash: d.deviceId ? djb2Hash(d.deviceId) : '',
            groupIdHash: d.groupId ? djb2Hash(d.groupId) : '',
            labelPresent: !!d.label,
          });
        }
        finish({
          devices, audioinput: summary.audioinput, audiooutput: summary.audiooutput,
          videoinput: summary.videoinput, labelsVisible, supported: true,
        });
      }).catch(() => finish(fallback));
    } catch {
      finish(fallback);
    }
  });
}

export function probePermissions(): Promise<Dict> {
  return new Promise((resolve) => {
    let done = false;
    const fallback = {
      geolocation: 'unknown', notifications: 'unknown',
      clipboardRead: 'unknown', clipboardWrite: 'unknown',
      persistentStorage: 'unknown', supported: false,
    };
    const finish = (v: Dict) => { if (done) return; done = true; resolve(v); };
    setTimeout(() => finish(fallback), 3000);
    try {
      if (!navigator.permissions || !navigator.permissions.query) {
        finish(fallback); return;
      }
      const queries = [
        { key: 'geolocation', opts: { name: 'geolocation' as PermissionName } },
        { key: 'notifications', opts: { name: 'notifications' as PermissionName } },
        { key: 'clipboardRead', opts: { name: 'clipboard-read' as PermissionName } },
        { key: 'clipboardWrite', opts: { name: 'clipboard-write' as PermissionName } },
        { key: 'persistentStorage', opts: { name: 'persistent-storage' as PermissionName } },
      ];
      const results: Dict = {
        geolocation: 'unknown', notifications: 'unknown',
        clipboardRead: 'unknown', clipboardWrite: 'unknown',
        persistentStorage: 'unknown', supported: true,
      };
      const promises = queries.map((q) => {
        try {
          return navigator.permissions.query(q.opts).then(
            (p) => { results[q.key] = p.state || 'unknown'; },
            () => { results[q.key] = 'unknown'; },
          );
        } catch {
          results[q.key] = 'unknown';
          return Promise.resolve();
        }
      });
      Promise.all(promises).then(() => finish(results));
    } catch {
      finish(fallback);
    }
  });
}

export function probeUACHLowEntropy(): Dict {
  try {
    const d = (navigator as unknown as { userAgentData?: { brands?: Dict[]; mobile?: boolean; platform?: string } }).userAgentData;
    if (!d) return { brands: [], mobile: false, platform: '', supported: false };
    return {
      brands: d.brands ? d.brands.slice() : [],
      mobile: !!d.mobile,
      platform: d.platform || '',
      supported: true,
    };
  } catch {
    return { brands: [], mobile: false, platform: '', supported: false };
  }
}

export function probeWindowSize(): Dict {
  try {
    return {
      innerWidth: window.innerWidth || 0,
      innerHeight: window.innerHeight || 0,
      outerWidth: window.outerWidth || 0,
      outerHeight: window.outerHeight || 0,
      screenX: typeof window.screenX === 'number' ? window.screenX : 0,
      screenY: typeof window.screenY === 'number' ? window.screenY : 0,
    };
  } catch {
    return { innerWidth: 0, innerHeight: 0, outerWidth: 0, outerHeight: 0, screenX: 0, screenY: 0 };
  }
}

export function probeOrientation(): Dict {
  try {
    const o = screen.orientation;
    if (!o) return { type: '', angle: 0, supported: false };
    return {
      type: o.type || '',
      angle: typeof o.angle === 'number' ? o.angle : 0,
      supported: true,
    };
  } catch {
    return { type: '', angle: 0, supported: false };
  }
}

export function probeMediaQueries(): Dict {
  const mq = (q: string): boolean => {
    try { return window.matchMedia(q).matches; } catch { return false; }
  };
  try {
    const colorScheme = mq('(prefers-color-scheme: dark)') ? 'dark'
      : mq('(prefers-color-scheme: light)') ? 'light' : 'no-preference';
    const reducedMotion = mq('(prefers-reduced-motion: reduce)') ? 'reduce' : 'no-preference';
    const contrast = mq('(prefers-contrast: more)') ? 'more'
      : mq('(prefers-contrast: less)') ? 'less'
      : mq('(prefers-contrast: custom)') ? 'custom' : 'no-preference';
    const forcedColors = mq('(forced-colors: active)') ? 'active' : 'none';
    const anyHover = mq('(any-hover: hover)') ? 'hover' : 'none';
    const anyPointer = mq('(any-pointer: fine)') ? 'fine'
      : mq('(any-pointer: coarse)') ? 'coarse' : 'none';
    const orientation = mq('(orientation: portrait)') ? 'portrait'
      : mq('(orientation: landscape)') ? 'landscape' : 'unknown';
    return { colorScheme, reducedMotion, contrast, forcedColors, anyHover, anyPointer, orientation };
  } catch {
    return {
      colorScheme: 'unknown', reducedMotion: 'unknown', contrast: 'unknown',
      forcedColors: 'unknown', anyHover: 'unknown', anyPointer: 'unknown',
      orientation: 'unknown',
    };
  }
}

export function probePlugins(): Dict {
  try {
    const list: Dict[] = [];
    const plugins = navigator.plugins;
    if (plugins) {
      for (let i = 0; i < plugins.length; i += 1) {
        const p = plugins[i]!;
        const mimes: Dict[] = [];
        for (let j = 0; j < p.length; j += 1) {
          const mt = p[j]!;
          mimes.push({ type: mt.type || '', suffixes: mt.suffixes || '', description: mt.description || '' });
        }
        list.push({
          name: p.name || '', filename: p.filename || '',
          description: p.description || '', mimeTypes: mimes,
        });
      }
    }
    let pdfEnabled: boolean | null = null;
    const pdf = (navigator as unknown as { pdfViewerEnabled?: boolean }).pdfViewerEnabled;
    if (typeof pdf === 'boolean') pdfEnabled = pdf;
    return { list, count: list.length, pdfViewerEnabled: pdfEnabled };
  } catch {
    return { list: [], count: 0, pdfViewerEnabled: null };
  }
}

// ---------------------------------------------------------------------------
// WebGL image hash (programmable pipeline + readPixels)
// ---------------------------------------------------------------------------
export function probeWebGLImageHash(): Dict {
  const result: Dict = { hash: '', status: 'init' };
  let gl: WebGLRenderingContext | null = null;
  let canvas: HTMLCanvasElement | null = null;
  try {
    canvas = attachedCanvas(256, 128);
    const opts: WebGLContextAttributes = { preserveDrawingBuffer: true, antialias: false };
    gl = (canvas.getContext('webgl', opts) as WebGLRenderingContext | null)
      ?? (canvas.getContext('experimental-webgl', opts) as WebGLRenderingContext | null);
    if (!gl) { result['status'] = 'no-ctx'; return result; }
    if (gl.isContextLost && gl.isContextLost()) { result['status'] = 'ctx-lost'; return result; }

    const vsSrc = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.0,1.0);}';
    const fsSrc = 'precision mediump float;void main(){gl_FragColor=vec4(gl_FragCoord.x/256.0,gl_FragCoord.y/128.0,0.5,1.0);}';
    const vs = gl.createShader(gl.VERTEX_SHADER)!;
    gl.shaderSource(vs, vsSrc); gl.compileShader(vs);
    const fs = gl.createShader(gl.FRAGMENT_SHADER)!;
    gl.shaderSource(fs, fsSrc); gl.compileShader(fs);
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs); gl.attachShader(prog, fs);
    gl.linkProgram(prog); gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    gl.viewport(0, 0, 256, 128);
    gl.clearColor(0, 0, 0, 1); gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    const pixels = new Uint8Array(256 * 128 * 4);
    gl.readPixels(0, 0, 256, 128, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    let s = '';
    for (let i = 0; i < pixels.length; i += 128) s += pixels[i]!.toString(16);
    result['hash'] = djb2Hash(s);
    result['status'] = 'ok';
  } catch (e) {
    result['status'] = `exception:${errMsg(e)}`;
  } finally {
    if (gl) {
      try {
        const lose = gl.getExtension('WEBGL_lose_context') as { loseContext(): void } | null;
        if (lose) lose.loseContext();
      } catch { /* ok */ }
    }
    detachCanvas(canvas);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Math signature (libm divergence)
// ---------------------------------------------------------------------------
export function probeMathSignature(): Dict {
  const result: Dict = { values: {} as Dict, hash: '', status: 'init' };
  try {
    const ops: Dict = {
      acos: Math.acos(0.123456789),
      acosh: Math.acosh(1e308),
      asinh: Math.asinh(1),
      atanh: Math.atanh(0.5),
      atan: Math.atan(0.5),
      sin: Math.sin(-1e300),
      sinh: Math.sinh(1),
      cos: Math.cos(10.000000000123456),
      cosh: Math.cosh(1),
      tan: Math.tan(-1e300),
      tanh: Math.tanh(1),
      exp: Math.exp(1),
      expm1: Math.expm1(1),
      log1p: Math.log1p(10),
      powPI: Math.pow(Math.PI, -100),
    };
    result['values'] = ops;
    const keys = Object.keys(ops).sort();
    let s = '';
    for (const k of keys) s += `${k}:${String(ops[k])}|`;
    result['hash'] = djb2Hash(s);
    result['status'] = 'ok';
  } catch (e) {
    result['status'] = `exception:${errMsg(e)}`;
  }
  return result;
}

// ---------------------------------------------------------------------------
// Font metrics (precise width per font)
// ---------------------------------------------------------------------------
export function probeFontMetrics(): Dict {
  const result: Dict = { metrics: {} as Dict, hash: '', status: 'init' };
  try {
    const testFonts = [
      'Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia',
      'Palatino', 'Garamond', 'Comic Sans MS', 'Impact', 'Trebuchet MS',
      'Lucida Console', 'Tahoma', 'Segoe UI', 'Calibri', 'Consolas',
      'Futura', 'Helvetica Neue', 'Monaco', 'Menlo', 'Gill Sans',
      'Lucida Grande', 'Avenir', 'Optima', 'Didot', 'Rockwell',
      'Franklin Gothic Medium', 'Candara', 'Cambria', 'Perpetua',
      'Book Antiqua',
    ];
    const testString = 'WwMmLli10!@#';
    const testSize = '48px';
    const baseFont = 'sans-serif';
    const canvas = document.createElement('canvas');
    canvas.width = 400; canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) { result['status'] = 'no-ctx'; return result; }

    const metrics = result['metrics'] as Dict;
    ctx.font = `${testSize} ${baseFont}`;
    metrics[`__base_${baseFont}`] = Math.round(ctx.measureText(testString).width * 100) / 100;
    for (const f of testFonts) {
      ctx.font = `${testSize} "${f}", ${baseFont}`;
      metrics[f] = Math.round(ctx.measureText(testString).width * 100) / 100;
    }
    const keys = Object.keys(metrics).sort();
    let s = '';
    for (const k of keys) s += `${k}:${String(metrics[k])}|`;
    result['hash'] = djb2Hash(s);
    result['status'] = 'ok';
  } catch (e) {
    result['status'] = `exception:${errMsg(e)}`;
  }
  return result;
}

// ---------------------------------------------------------------------------
// Emoji hash (OS emoji-font version signal)
// ---------------------------------------------------------------------------
export function probeEmojiHash(): Dict {
  const result: Dict = { hash: '', widths: [] as number[], status: 'init' };
  try {
    const emojis = [
      '\uD83E\uDDD1\u200D\uD83D\uDE80',
      '\uD83C\uDFF3\uFE0F\u200D\uD83C\uDF08',
      '\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67\u200D\uD83D\uDC66',
      '\uD83E\uDEE8',
      '\uD83E\uDEE0',
      '\uD83E\uDEE1',
      '\uD83C\uDDE8\uD83C\uDDF3',
      '\uD83D\uDC4D\uD83C\uDFFF',
      '\uD83E\uDDD1\uD83C\uDFFB\u200D\uD83D\uDCBB',
    ];
    const canvas = document.createElement('canvas');
    canvas.width = 200; canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) { result['status'] = 'no-ctx'; return result; }
    ctx.textBaseline = 'top';
    ctx.font = '28px sans-serif';
    const widths: number[] = [];
    for (const e of emojis) {
      widths.push(Math.round(ctx.measureText(e).width * 100) / 100);
    }
    result['widths'] = widths;
    ctx.clearRect(0, 0, 200, 50);
    ctx.fillStyle = '#000';
    ctx.fillText(emojis.join(''), 0, 0);
    let raster = '';
    try { raster = canvas.toDataURL('image/png'); } catch { /* tainted */ }
    result['hash'] = djb2Hash(`${raster}|${widths.join(',')}`);
    result['status'] = 'ok';
  } catch (e) {
    result['status'] = `exception:${errMsg(e)}`;
  }
  return result;
}

// ---------------------------------------------------------------------------
// Environment diagnostics
// ---------------------------------------------------------------------------
export function probeEnvironment(): Dict {
  const env: Dict = {};
  try {
    env['isSecureContext'] = typeof window.isSecureContext !== 'undefined' ? !!window.isSecureContext : null;
    env['protocol'] = location.protocol || '';
    env['hostname'] = location.hostname || '';
    env['port'] = location.port || '';
    env['origin'] = location.origin || '';
    env['typeofAudioContext'] = typeof window.AudioContext;
    env['typeofWebkitAudioContext'] = typeof window.webkitAudioContext;
    env['typeofOfflineAudioContext'] = typeof window.OfflineAudioContext;
    env['typeofWebGLRenderingContext'] = typeof window.WebGLRenderingContext;
    env['typeofWebGL2RenderingContext'] = typeof window.WebGL2RenderingContext;
    env['typeofRTCPeerConnection'] = typeof window.RTCPeerConnection;
    env['typeofMediaDevices'] = typeof navigator.mediaDevices;
    env['typeofPermissions'] = typeof navigator.permissions;
  } catch (e) {
    env['error'] = errMsg(e);
  }
  return env;
}

// ---------------------------------------------------------------------------
// WebGPU adapter info
// adapter.info is Chrome 114+; older builds expose it through the
// requestAdapterInfo() Promise. Adapter creation can hang in restricted
// frames — race against a 3s watchdog so the whole collection isn't blocked.
// Per scripts/collect_device_fingerprint.py:147-161, the same five fields
// (vendor / architecture / device / description / features / limits) are the
// canonical capture surface.
// ---------------------------------------------------------------------------
type WebGPUAdapterInfoLike = {
  vendor?: string;
  architecture?: string;
  device?: string;
  description?: string;
};

type WebGPUAdapterLike = {
  info?: WebGPUAdapterInfoLike;
  requestAdapterInfo?: () => Promise<WebGPUAdapterInfoLike>;
  features?: Iterable<string>;
  limits?: Record<string, number>;
};

export async function probeWebGPU(): Promise<Dict> {
  const fallback: Dict = {
    supported: false,
    vendor: '',
    architecture: '',
    device: '',
    description: '',
    features: [] as string[],
    limits: {} as Record<string, number>,
    status: 'init',
  };

  const gpu = (navigator as unknown as {
    gpu?: { requestAdapter(): Promise<WebGPUAdapterLike | null> };
  }).gpu;
  if (!gpu || typeof gpu.requestAdapter !== 'function') {
    return { ...fallback, status: 'no-api' };
  }

  // requestAdapter can hang in iframe / headless / discrete-GPU disabled
  // contexts. 3s race keeps the wider collection responsive.
  const adapter = await Promise.race<WebGPUAdapterLike | null>([
    gpu.requestAdapter().catch(() => null),
    new Promise<null>((r) => setTimeout(() => r(null), 3000)),
  ]);
  if (!adapter) {
    return { ...fallback, supported: true, status: 'no-adapter-or-timeout' };
  }

  // Chrome 114+ exposes adapter.info, but `device` + `description` come back
  // empty unless the page is in a secure context with the WebGPU privacy
  // origin trial enabled — Chrome strips these by default to limit GPU
  // model fingerprinting. Empty values for those two fields on stock
  // Chrome are expected, not a probe bug. `vendor` + `architecture` always
  // populate.
  let info: WebGPUAdapterInfoLike = adapter.info ?? {};
  if (!info.vendor && typeof adapter.requestAdapterInfo === 'function') {
    try {
      info = (await adapter.requestAdapterInfo()) ?? {};
    } catch {
      /* keep partial */
    }
  }

  const featuresOut: string[] = [];
  try {
    for (const f of adapter.features ?? []) featuresOut.push(String(f));
    featuresOut.sort();
  } catch {
    /* leave empty */
  }

  // GPUSupportedLimits exposes its values as prototype-side getters, not own
  // enumerable properties — `Object.entries(adapter.limits)` returns []. The
  // WebGPU spec lists these names; we read each one directly. Real-world
  // counts: ~32 fields on Chrome 130+, all returning finite numbers.
  // (The same bug lives in scripts/collect_device_fingerprint.py:158 — fix
  // there in a follow-up if that path stays in active use.)
  const LIMIT_KEYS = [
    'maxTextureDimension1D', 'maxTextureDimension2D', 'maxTextureDimension3D',
    'maxTextureArrayLayers', 'maxBindGroups', 'maxBindGroupsPlusVertexBuffers',
    'maxBindingsPerBindGroup', 'maxDynamicUniformBuffersPerPipelineLayout',
    'maxDynamicStorageBuffersPerPipelineLayout', 'maxSampledTexturesPerShaderStage',
    'maxSamplersPerShaderStage', 'maxStorageBuffersPerShaderStage',
    'maxStorageTexturesPerShaderStage', 'maxUniformBuffersPerShaderStage',
    'maxUniformBufferBindingSize', 'maxStorageBufferBindingSize',
    'minUniformBufferOffsetAlignment', 'minStorageBufferOffsetAlignment',
    'maxVertexBuffers', 'maxBufferSize', 'maxVertexAttributes',
    'maxVertexBufferArrayStride', 'maxInterStageShaderComponents',
    'maxInterStageShaderVariables', 'maxColorAttachments',
    'maxColorAttachmentBytesPerSample', 'maxComputeWorkgroupStorageSize',
    'maxComputeInvocationsPerWorkgroup', 'maxComputeWorkgroupSizeX',
    'maxComputeWorkgroupSizeY', 'maxComputeWorkgroupSizeZ',
    'maxComputeWorkgroupsPerDimension',
  ] as const;
  const limitsOut: Record<string, number> = {};
  try {
    const lim = adapter.limits as unknown as Record<string, unknown> | undefined;
    if (lim) {
      for (const k of LIMIT_KEYS) {
        const v = lim[k];
        if (typeof v === 'number' && Number.isFinite(v)) limitsOut[k] = v;
      }
    }
  } catch {
    /* leave empty */
  }

  return {
    supported: true,
    vendor: info.vendor ?? '',
    architecture: info.architecture ?? '',
    device: info.device ?? '',
    description: info.description ?? '',
    features: featuresOut,
    limits: limitsOut,
    status: 'ok',
  };
}

function errMsg(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return String(e);
}
